import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';
import { HisBackendService } from '../../core/service/backend/his-backend.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { GenericValue } from '../../core/model/generic-value';
import { Language } from '../model/language';

@Injectable({providedIn: 'root'})
export class HisTranslationService {
    private hisBaseGlobalsService = inject(HisBaseGlobalsService);
    private hisBackendService = inject(HisBackendService);
    private hisUiService = inject(HisUiService);


    private translationLanguage = new BehaviorSubject<Language>( undefined );
    translationLanguage$ = this.translationLanguage.asObservable();

    /**
     * Translates the attribute from an object to another language if the _foreigntext is correctly filled
     *
     * @param object - the object with translatable attributes
     * @param attributeName - name of the attribute
     * @param language - the language to translate to (if not given the currently selected language from HisBaseGlobalsService is used)
     */
    translate( hisObject: any, hisAttribute: string, language?: string ): string {

        // if language is not given, we'll use the language from hisBaseGlobalsService
        if ( !language ) {
            language = this.hisBaseGlobalsService.getLanguage();
        }

        // if the wanted language is not existing, the default value will be returned
        if ( this.isTranslationExisting( hisObject, hisAttribute, language ) ) {
            return hisObject._foreigntext[language][hisAttribute];
        } else if ( hisObject[hisAttribute] ) {
            return hisObject[hisAttribute];
        } else if ( hisObject[hisAttribute] === undefined ) {
            console.warn( 'No attribute named \'', hisAttribute, '\' present in object ', hisObject );
        }
        return undefined;
    }


    /**
     * Returns all possible translation languages as an array.
     * First entry of this array always contains the manipulated defaultLanguage (defaultLanguage.text + translation.defaultLanguagePrefix)
     */
    public translationsLanguages( defaultLanguage: Language ): Language[] {
        let languages = [];
        // add the defaultLanguage at index 0
        languages.push(
            new Language(
                defaultLanguage.id,
                defaultLanguage.text + ' ' + this.hisUiService.translate( 'translation.defaultLanguage.suffix' ),
                defaultLanguage.uniquename,
                defaultLanguage.uniquename ) );

        let langCodes = this.hisBaseGlobalsService.translationLanguages.split( ',' );
        for ( let langCode of langCodes ) {
            this.hisBackendService.getData( 'api/v1/cs/sys/values/languageValue?uniquename=' + langCode ).subscribe( (langs: GenericValue[]) => {
                for ( let lang of langs ) {
                    if ( lang.uniquename === langCode && lang.id !== defaultLanguage.id ) {
                        languages.push( new Language( lang.id, lang.text, lang.uniquename, lang.uniquename ) );
                    }
                }
            } );
        }
        return languages;
    }

    public setTranslationLanguage( translationLanguage: Language ) {
        this.translationLanguage.next( translationLanguage );
    }

    /** Set the value of the given hisAttribute in the currently active language. */
    public setHisObjectActiveAttribute( hisObject: any, hisAttribute: string, newValue: any ) {
        if ( !this.translationLanguage.value ) {
            hisObject[hisAttribute] = newValue;
        } else {
            this.ensureForeigntextNotNull( hisObject, hisAttribute );
            hisObject._foreigntext[this.translationLanguage.value.iso_639_1][hisAttribute] = newValue;
            this.deleteEmptyForeigntext( hisObject, hisAttribute );
        }
    }

    /** Return the value of the given hisAttribute in the currently active language.
     *  If it is not existing yet, it will be initialized as empty string
     */
    public getHisObjectActiveAttribute( hisObject: any, hisAttribute: string ): any {
        if ( !this.translationLanguage.value ) {
            // return hisAttribute in defaultLanguage
            return hisObject[hisAttribute] ? hisObject[hisAttribute] : '';
        } else {
            if ( this.isTranslationExisting( hisObject, hisAttribute, this.translationLanguage.value.uniquename ) ) {
                // return hisAttribute in translationLanguage
                return hisObject._foreigntext[this.translationLanguage.value.iso_639_1][hisAttribute];
            }
            // return empty string
            return '';
        }
    }

    /** Ensures that the given attribute is given for the current translationLanguage */
    private ensureForeigntextNotNull( hisObject: any, hisAttribute: string ) {
        if ( !hisObject._foreigntext ) {
            hisObject._foreigntext = {};
        }
        if ( !hisObject._foreigntext[this.translationLanguage.value.iso_639_1] ) {
            // initialize empty object for the specific language
            hisObject._foreigntext[this.translationLanguage.value.iso_639_1] = {};
        }

        if ( !hisObject._foreigntext[this.translationLanguage.value.iso_639_1][hisAttribute] ) {
            // initialize it as empty string
            hisObject._foreigntext[this.translationLanguage.value.iso_639_1][hisAttribute] = '';
        }
    }

    /** Removes empty foreigntext */
    private deleteEmptyForeigntext( hisObject: any, hisAttribute: string ) {
        if ( !hisObject._foreigntext ) {
            return;
        }

        if ( hisObject._foreigntext[this.translationLanguage.value.iso_639_1][hisAttribute] === '' ) {
            // delete empty translation on lowest level (attribute)
            delete hisObject._foreigntext[this.translationLanguage.value.iso_639_1][hisAttribute];
        }

        if ( Object.keys( hisObject._foreigntext[this.translationLanguage.value.iso_639_1] ).length === 0 ) {
            // delete empty translation on mid level (language)
            delete hisObject._foreigntext[this.translationLanguage.value.iso_639_1];
        }

    }

    /**
     * Returns true if the wanted translation is given in _foreigntexts
     */
    public isTranslationExisting( hisObject: any, hisAttribute: string, language: string ): boolean {
        return hisObject._foreigntext &&
                hisObject._foreigntext[language] &&
                hisObject._foreigntext[language][hisAttribute] &&
                hisObject._foreigntext[language][hisAttribute].length>0 ;
    }
}
